/**
 * Nodo interno del árbol AVL.
 */
export interface AVLNode<T> {
  value: T
  left: AVLNode<T> | null
  right: AVLNode<T> | null
  height: number
}

/**
 * Árbol AVL — árbol binario de búsqueda auto-balanceado.
 * Garantiza O(log n) en inserción, búsqueda y eliminación independientemente del orden de inserción.
 * El factor de balance (altura izquierda − derecha) se mantiene entre −1 y 1 en todo momento.
 * @template T - El tipo de los valores almacenados.
 */
export class AVLTree<T> {
  root: AVLNode<T> | null = null
  #size = 0
  #didDelete = false
  readonly #comparator: (a: T, b: T) => number

  /**
   * @param comparator - Función de comparación opcional (a, b) => number.
   *   Por defecto usa el operador < para primitivos.
   */
  constructor(comparator?: (a: T, b: T) => number) {
    this.#comparator = comparator ?? ((a, b) => {
      if (a < b) return -1
      if (a > b) return 1
      return 0
    })
  }

  /**
   * Inserta un valor en el árbol.
   * Si el valor ya existe (según el comparador), lo actualiza.
   */
  insert(value: T): void {
    this.root = this.#insert(this.root, value)
  }

  /**
   * Busca si un valor existe en el árbol.
   */
  search(value: T): boolean {
    let current = this.root
    while (current !== null) {
      const cmp = this.#comparator(value, current.value)
      if (cmp === 0) return true
      current = cmp < 0 ? current.left : current.right
    }
    return false
  }

  /**
   * Elimina un valor del árbol. El árbol se rebalancea automáticamente.
   * @returns true si el valor existía y fue eliminado, false si no existía.
   */
  delete(value: T): boolean {
    this.#didDelete = false
    this.root = this.#delete(this.root, value)
    if (this.#didDelete) this.#size--
    return this.#didDelete
  }

  /**
   * Retorna el valor mínimo del árbol.
   */
  min(): T | undefined {
    if (!this.root) return undefined
    return this.#minNode(this.root).value
  }

  /**
   * Retorna el valor máximo del árbol.
   */
  max(): T | undefined {
    if (!this.root) return undefined
    let node = this.root
    while (node.right) node = node.right
    return node.value
  }

  /**
   * Traversal en orden (izquierda → raíz → derecha).
   * Retorna los valores en orden ascendente según el comparador.
   */
  inOrder(): T[] {
    const result: T[] = []
    this.#inOrder(this.root, result)
    return result
  }

  /**
   * Traversal pre-orden (raíz → izquierda → derecha).
   */
  preOrder(): T[] {
    const result: T[] = []
    this.#preOrder(this.root, result)
    return result
  }

  /**
   * Traversal post-orden (izquierda → derecha → raíz).
   */
  postOrder(): T[] {
    const result: T[] = []
    this.#postOrder(this.root, result)
    return result
  }

  /**
   * Número de nodos en el árbol.
   */
  get size(): number {
    return this.#size
  }

  /**
   * Indica si el árbol está vacío.
   */
  isEmpty(): boolean {
    return this.#size === 0
  }

  /**
   * Vacía el árbol completamente.
   */
  clear(): void {
    this.root = null
    this.#size = 0
  }

  // ── Rotaciones y balance ──────────────────────────────────────────────

  #height(node: AVLNode<T> | null): number {
    return node?.height ?? 0
  }

  #balanceFactor(node: AVLNode<T>): number {
    return this.#height(node.left) - this.#height(node.right)
  }

  #updateHeight(node: AVLNode<T>): void {
    node.height = 1 + Math.max(this.#height(node.left), this.#height(node.right))
  }

  /**
   * Rotación simple a la derecha (caso Izquierda-Izquierda).
   */
  #rotateRight(y: AVLNode<T>): AVLNode<T> {
    const x = y.left!
    const temp = x.right
    x.right = y
    y.left = temp
    this.#updateHeight(y)
    this.#updateHeight(x)
    return x
  }

  /**
   * Rotación simple a la izquierda (caso Derecha-Derecha).
   */
  #rotateLeft(x: AVLNode<T>): AVLNode<T> {
    const y = x.right!
    const temp = y.left
    y.left = x
    x.right = temp
    this.#updateHeight(x)
    this.#updateHeight(y)
    return y
  }

  /**
   * Rebalancea el nodo aplicando la rotación correspondiente según el factor de balance.
   */
  #balance(node: AVLNode<T>): AVLNode<T> {
    this.#updateHeight(node)
    const bf = this.#balanceFactor(node)
    if (bf > 1) {
      if (this.#balanceFactor(node.left!) < 0) {
        node.left = this.#rotateLeft(node.left!)   // Caso Izquierda-Derecha
      }
      return this.#rotateRight(node)               // Caso Izquierda-Izquierda
    }
    if (bf < -1) {
      if (this.#balanceFactor(node.right!) > 0) {
        node.right = this.#rotateRight(node.right!) // Caso Derecha-Izquierda
      }
      return this.#rotateLeft(node)                 // Caso Derecha-Derecha
    }
    return node
  }

  // ── Operaciones recursivas internas ───────────────────────────────────

  #insert(node: AVLNode<T> | null, value: T): AVLNode<T> {
    if (!node) {
      this.#size++
      return { value, left: null, right: null, height: 1 }
    }
    const cmp = this.#comparator(value, node.value)
    if (cmp < 0) {
      node.left = this.#insert(node.left, value)
    } else if (cmp > 0) {
      node.right = this.#insert(node.right, value)
    } else {
      node.value = value  // Actualizar duplicado sin cambiar el tamaño
      return node
    }
    return this.#balance(node)
  }

  #minNode(node: AVLNode<T>): AVLNode<T> {
    let current = node
    while (current.left) current = current.left
    return current
  }

  #delete(node: AVLNode<T> | null, value: T): AVLNode<T> | null {
    if (!node) return null
    const cmp = this.#comparator(value, node.value)
    if (cmp < 0) {
      node.left = this.#delete(node.left, value)
    } else if (cmp > 0) {
      node.right = this.#delete(node.right, value)
    } else {
      this.#didDelete = true
      if (!node.left) return node.right
      if (!node.right) return node.left
      // Dos hijos: reemplazar con el sucesor en orden (mínimo del subárbol derecho)
      const successor = this.#minNode(node.right)
      node.value = successor.value
      this.#didDelete = false  // Se volverá true al eliminar el sucesor en la llamada recursiva
      node.right = this.#delete(node.right, successor.value)
    }
    return this.#balance(node)
  }

  #inOrder(node: AVLNode<T> | null, result: T[]): void {
    if (!node) return
    this.#inOrder(node.left, result)
    result.push(node.value)
    this.#inOrder(node.right, result)
  }

  #preOrder(node: AVLNode<T> | null, result: T[]): void {
    if (!node) return
    result.push(node.value)
    this.#preOrder(node.left, result)
    this.#preOrder(node.right, result)
  }

  #postOrder(node: AVLNode<T> | null, result: T[]): void {
    if (!node) return
    this.#postOrder(node.left, result)
    this.#postOrder(node.right, result)
    result.push(node.value)
  }
}
